import SuccessBanner from "components/Banner/SuccessBanner";
import { ErrorBanner } from "components/Error/ErrorBanner";
import { ButtonGroupField } from "components/Forms/ButtonGroup";
import DateRangePicker from "components/Forms/DateRangePicker/DateRangePicker";
import BlockSpinner from "components/Spinners/BlockSpinner";
import { Button } from "components/Theme/button";
import { Heading } from "components/Theme/heading";
import DisplayIfUserType from "components/utils/DisplayIfUserType/DisplayIfUserType";
import { Form, Formik, type FormikHelpers } from "formik";
import { type AssetPosition, assetPositionActionTypesEnum } from "kubb";
import type React from "react";
import { useState } from "react";
import * as Yup from "yup";
import { useCreateAssetPositionAction } from "../../../hooks/createEntity";

interface FormValues {
  start_datetime: string;
  end_datetime: string;
  remove_quarantine: boolean;
}

const validationSchema = Yup.object().shape({
  start_datetime: Yup.string().required("Start date is required"),
  end_datetime: Yup.string().required("End date is required"),
});

const initialValues: FormValues = {
  start_datetime: "",
  end_datetime: "",
  remove_quarantine: false,
};

export default function WirelessClusterReprocessTab(props: {
  clusterPosition: AssetPosition;
}): React.ReactElement {
  const { clusterPosition } = props;
  const action = useCreateAssetPositionAction(clusterPosition);
  const [successKey, setSuccessKey] = useState<number | null>(null);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>,
  ) => {
    setSuccessKey(null);
    try {
      await action.update({
        action_type: assetPositionActionTypesEnum.REPROCESS_CLUSTER_HISTORICAL,
        payload: {
          start_datetime: values.start_datetime,
          end_datetime: values.end_datetime,
          remove_quarantine: values.remove_quarantine,
        },
      });
      setSuccessKey(Date.now());
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DisplayIfUserType userTypes={"superadmin"}>
      <Heading>Reprocess Cluster Historical Data</Heading>
      <p className="text-sm text-gray-600 mb-4">
        Triggers a historical reprocess on every gateway in this cluster.
        Streaming gateways replay MQTT content; file-based gateways resend
        files. Per-gateway date fan-out is handled downstream.
      </p>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ isSubmitting, values }): React.ReactElement => (
          <BlockSpinner loading={action.loading}>
            <Form data-cy={"wireless-cluster-reprocess"}>
              <DateRangePicker
                startDateName="start_datetime"
                endDateName="end_datetime"
                useStringValues={true}
                label={{
                  label: "Date range",
                  description: "Date range to reprocess across the cluster.",
                }}
              />
              <ButtonGroupField
                fieldName="remove_quarantine"
                label={{
                  label:
                    "Remove quarantine before resending file-based gateways",
                }}
                options={[
                  { label: "Off", value: false },
                  { label: "On", value: true },
                ]}
              />
              <Button
                type={"submit"}
                color={"brandLight"}
                disabled={
                  isSubmitting ||
                  !values.start_datetime ||
                  !values.end_datetime ||
                  action.loading
                }
                className="mt-4"
              >
                Reprocess cluster
              </Button>
              {successKey !== null && !action.error && (
                <SuccessBanner key={successKey} title="Reprocess queued">
                  Per-gateway reprocess commands have been dispatched.
                </SuccessBanner>
              )}
              <ErrorBanner error={action.error} />
            </Form>
          </BlockSpinner>
        )}
      </Formik>
    </DisplayIfUserType>
  );
}
